// ใบลา — the leave REQUEST lifecycle. Pure (no I/O).
//
// ⚠️ The QUOTA maths is NOT here. It lives in ./leave.js (personalEntitlement,
// sickEntitlement, leaveQuota) and there must only ever be one copy: Employee
// carries a `leavequota` column that is an older, different answer, and the HR
// editor already dropped its field because of exactly that. A second
// implementation would diverge the first time somebody edited that column.
//
// Backend: Xano table leave_request (97) in the `claude` group, via
// /api/me/leave (employee) and /api/hr/leave-request (HR/managers).
// Approval writes employee_timetable rows through api 290 / api 354 — never
// directly — so the live Bubble roster keeps receiving its realtime events.
import { LEAVE_PERSONAL, LEAVE_SICK, leaveTypeMeta } from './timetable.js'

export { LEAVE_PERSONAL, LEAVE_SICK, leaveTypeMeta }

// ลากิจ past this many consecutive days is SUBMITTABLE — the owner's instruction is
// explicit that an employee may ask and that there is no guarantee — but it carries
// a warning on the form and a flag into HR's queue, because ฝ่ายบุคคล want to meet
// the person before granting it. It is a review threshold, never a validation rule.
export const PERSONAL_REVIEW_DAYS = 3
// ลาป่วย past this many days should carry a medical certificate. Advisory only:
// someone genuinely ill on day four must not be stopped by a form.
export const SICK_CERT_DAYS = 3
// One payroll-visible month. Matches MAX_RANGE_DAYS in ./clockin.js, and is what
// keeps a single approval inside Cloudflare's 50-subrequest ceiling — the approval
// reads the window once and then writes one employee_timetable row per day.
export const MAX_SPAN_DAYS = 31

export const LEAVE_STATUS = {
  pending: 'รอพิจารณา',
  approved: 'อนุมัติ',
  rejected: 'ไม่อนุมัติ',
  cancelled: 'ยกเลิกแล้ว',
}

export const STATUS_TONE = {
  pending: 'bg-amber-50 text-amber-700 ring-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 ring-rose-200',
  cancelled: 'bg-slate-100 text-slate-500 ring-slate-200',
}

export const statusLabel = (s) => LEAVE_STATUS[s] || s || '—'
export const statusTone = (s) => STATUS_TONE[s] || STATUS_TONE.cancelled

/**
 * Inclusive day count over two 'YYYY-MM-DD' strings.
 *
 * Parsed as UTC midnight and stepped in whole UTC days, so it cannot drift across
 * a month or year boundary — and Bangkok has no DST, so there is no local-time
 * hazard either. Returns 0 for anything unparseable.
 */
export function spanDays(from, to) {
  const a = Date.parse(`${from}T00:00:00Z`)
  const b = Date.parse(`${to || from}T00:00:00Z`)
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 0
  return Math.round((b - a) / 86400000) + 1
}

// Inclusive list of 'YYYY-MM-DD' days, capped so a typo cannot render 4,000 rows.
export function dateRange(from, to) {
  const out = []
  let t = Date.parse(`${from}T00:00:00Z`)
  const end = Date.parse(`${to || from}T00:00:00Z`)
  if (!Number.isFinite(t) || !Number.isFinite(end) || end < t) return out
  while (t <= end && out.length <= MAX_SPAN_DAYS) {
    out.push(new Date(t).toISOString().slice(0, 10))
    t += 86400000
  }
  return out
}

/**
 * Everything the form and the decision drawer need to say about a draft, in one
 * object, so the two screens cannot disagree about the same request.
 *
 * `blocking` is ONLY ever the span cap. The ลากิจ rule is advisory by the owner's
 * explicit decision: "user can try to submit the longer period if they think they
 * need it but no guarantee, and HR have to get a meeting first."
 */
export function assessLeave({ type, date_from, date_to, fileCount = 0 }) {
  const days = spanDays(date_from, date_to)
  const notes = []
  let blocking = null

  if (!date_from) {
    blocking = 'กรุณาระบุวันที่เริ่มลา'
  } else if (days < 1) {
    blocking = 'วันสิ้นสุดต้องไม่อยู่ก่อนวันเริ่ม'
  } else if (days > MAX_SPAN_DAYS) {
    blocking = `ลาต่อเนื่องได้ไม่เกิน ${MAX_SPAN_DAYS} วันต่อหนึ่งใบ หากยาวกว่านี้กรุณาติดต่อฝ่ายบุคคลโดยตรง`
  }

  const needsMeeting = type === LEAVE_PERSONAL && days > PERSONAL_REVIEW_DAYS
  if (needsMeeting) {
    notes.push({
      tone: 'amber',
      text: `ลากิจติดต่อกันเกิน ${PERSONAL_REVIEW_DAYS} วัน — ส่งคำขอได้ แต่ฝ่ายบุคคลจะขอนัดคุยก่อนพิจารณา และอาจไม่ได้รับอนุมัติ`,
    })
  }

  const wantsCertificate = type === LEAVE_SICK && days > SICK_CERT_DAYS
  if (wantsCertificate && fileCount === 0) {
    notes.push({
      tone: 'amber',
      text: `ลาป่วยติดต่อกันเกิน ${SICK_CERT_DAYS} วัน — กรุณาแนบใบรับรองแพทย์เพื่อประกอบการพิจารณา`,
    })
  }

  return { days, blocking, notes, needsMeeting, wantsCertificate, hasCertificate: fileCount > 0 }
}

// "22/08/2026" or "22/08/2026 – 26/08/2026" — ค.ศ. dd/mm/yyyy, the format every
// HR and payroll screen in this app uses (fmtDateTH's Buddhist era is for
// generated documents only; see project.md §Dates).
export function fmtRange(from, to) {
  const one = (v) => {
    const d = new Date(`${v}T00:00:00Z`)
    if (Number.isNaN(d.getTime())) return '—'
    const p = (n) => String(n).padStart(2, '0')
    return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`
  }
  if (!from) return '—'
  if (!to || to === from) return one(from)
  return `${one(from)} – ${one(to)}`
}

// Today in Bangkok as 'YYYY-MM-DD', for date-input minimums and defaults.
export function bkkToday(now = Date.now()) {
  return new Date(now + 7 * 3600 * 1000).toISOString().slice(0, 10)
}

// A leave whose decision the employee has not opened yet — drives the /me dot.
export const isUnseenDecision = (r) =>
  r && r.status !== 'pending' && r.status !== 'cancelled' && !r.decision_seen_at

// A pending request may be withdrawn by the employee. Once decided, unwinding is
// HR's job (the timetable rows already exist), which the UI says out loud.
export const canCancel = (r) => r?.status === 'pending'

/**
 * Why an approved request may not have reached the roster.
 *
 * `applied: false` on an approved row means the decision was recorded without the
 * proxy loop running — a direct Xano call, or a failure between the two writes.
 * It is surfaced rather than hidden: a leave everyone believes is on the roster,
 * and is not, is the expensive version of this bug.
 */
export function applyWarning(r) {
  if (r?.status !== 'approved') return null
  if (!r.applied) return 'อนุมัติแล้ว แต่ยังไม่ได้ลงในตารางเวร — กรุณาตรวจสอบตารางเวร'
  if (r.partial) {
    const n = Array.isArray(r.skipped) ? r.skipped.length : 0
    return `ลงตารางเวรแล้ว แต่มี ${n} วันที่ข้ามไป — ดูรายละเอียดด้านล่าง`
  }
  return null
}

export const SKIP_REASON_TH = {
  punched: 'มีการลงเวลาไว้แล้ว',
  exists: 'มีตารางเวรวันนี้อยู่แล้ว',
  patch_failed: 'แก้ไขตารางเวรไม่สำเร็จ',
}
export const skipReason = (s) => SKIP_REASON_TH[s?.reason] || s?.detail || s?.reason || '—'
